import * as React from 'react';
import styled, { css } from 'styled-components';
import Lightbox from '../lightbox/Lightbox';
import { imageIcon } from '../ImageIcon/ImageIcon';
import ButtonWithImage from '../button/buttonWithImage';
import { withTheme } from '../themeProvider/ThemeProvider';

const SPopup = styled.section<{ customHeight?: string; showCloseButton: boolean }>(
  ({ theme, customHeight, showCloseButton }) => `
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: ${theme.colors.white};
  box-shadow: 0 0 28px 16px rgba(0, 0, 0, 0.12);
  padding-top: ${showCloseButton ? 37 : 27}px;
  box-sizing: border-box;
  border-radius: 5px;
  text-align: center;
  border-radius: 15px;
  width: 310px;
  @media only screen and ${theme.device.laptopS} {
    box-sizing: border-box;
    ${customHeight ? `height: ${customHeight}` : ''}
    width: 525px;
    padding: 40px 60px;
  }
`
);

const SPopupText = styled.div(
  ({ theme }) => `
   padding: 0 38px;
   font-family: Poppins;
   font-size: 14px;
   font-weight: 400;
   line-height: 1.21;
   color: ${theme.colors.primaryBackground};
   text-align: left;
`
);

const Toolbox = styled.div(
  ({ theme }) => css`
    display: flex;
    position: absolute;
    top: 10px;
    right: 20px;
  `
);

const SPopupBoldText = styled.div(
  ({ theme }) => `
   padding: 0 28px;
   font-family: Poppins;
   font-size: 18px;
   font-weight: 700;
   line-height: 1.41;
   white-space: pre-line;
   color: ${theme.colors.primaryBackground};
   margin-bottom: 10px;
`
);

const SButtonsLine = styled.div<{ singleButton; expanded }>(
  ({ singleButton, expanded }) => `
  display: flex;
  padding: 0 20px;
  margin-top: ${expanded ? '100px' : '0'};

  margin-bottom: 16px;
  justify-content: ${singleButton ? 'center' : 'space-between'};
`
);

const SPhoneIcon = styled.div(({ theme }) => ({
  'margin-right': '10px',
  'margin-top': '5px',
  '&:focus': { outline: 'solid' },
  '&:before': {
    ...imageIcon({ icon: theme.icons.phoneWhite, height: 24, width: 24, color: '' }),
  },
}));

const SEmailIcon = styled.div(({ theme }) => ({
  'margin-right': '10px',
  'margin-top': '5px',
  '&:focus': { outline: 'solid' },
  '&:before': {
    ...imageIcon({ icon: theme.icons.emailWhite, height: 24, width: 24, color: '' }),
  },
}));

const SCloseIcon = styled.div(({ theme }) => ({
  position: 'absolute',
  top: '10px',
  left: '12px',
  cursor: 'pointer',
  opacity: '0.7',
  ...imageIcon({ icon: theme.icons.close, height: 24, width: 24, color: theme.colors.mediumGray }),
  '&:hover': {
    opacity: 0.8,
  },
}));

const Note = styled.p(
  ({ theme }) => css`
    font-size: 12px;
    color: ${theme.colors.primaryBackground};
    padding: 0 20px;

    @media only screen and ${theme.device.max.laptopS} {
      margin-bottom: 28px;
    }
  `
);

export interface IPopup {
  className?: string;
  customHeight?: string;
  grayButtonAction?: () => void;
  blueButtonAction?: () => void;
  isBlueButtonDisabled?: boolean;
  boldText: string;
  grayButtonText?: string;
  blueButtonText?: string;
  blueButtonImg?: string;
  expanded?: boolean;
  dataAt: string;
  onClose?: () => void;
  noFooterButtons?: boolean;
  TopToolboxContent?: React.ReactChildren;
  noLightBox?: boolean;
  dataTestId?: string;
  noteText?: string;
}

// tslint:disable-next-line:variable-name
const Popup: React.FC<IPopup> = ({
  grayButtonAction,
  blueButtonAction,
  customHeight,
  className,
  isBlueButtonDisabled,
  grayButtonText,
  blueButtonText,
  blueButtonImg,
  boldText,
  children,
  expanded,
  dataAt,
  onClose,
  noFooterButtons,
  TopToolboxContent,
  noLightBox,
  dataTestId,
  noteText,
}) => {
  const handleGrayButton = React.useCallback(() => {
    if (grayButtonAction) {
      grayButtonAction();
    }
  }, [grayButtonAction]);

  const handleBlueButton = React.useCallback(() => {
    blueButtonAction();
  }, [blueButtonAction]);

  const showCloseButton = !!onClose;

  const popup = (
    <SPopup
      {...{ className, 'data-at': dataAt, customHeight, showCloseButton, 'data-testid': dataTestId, role: 'dialog' }}
    >
      {showCloseButton && <SCloseIcon className="close-button" onClick={onClose} />}
      {TopToolboxContent && <Toolbox className="top-toolbox">{TopToolboxContent}</Toolbox>}
      <SPopupBoldText data-testid="dialog_title" data-at="dialogTitle" className="dialog-title">
        {boldText}
      </SPopupBoldText>
      <SPopupText className="popup-content" data-at="dialogText">
        {children}
      </SPopupText>
      {!noFooterButtons && (
        <SButtonsLine {...{ className: 'buttons-row', singleButton: !Boolean(grayButtonAction), expanded }}>
          {Boolean(grayButtonAction) && (
            <ButtonWithImage
              {...{
                buttonTheme: 'gray',
                onClick: handleGrayButton,
                'data-at': 'actionButton2',
                'data-testid': 'popup-cancel-button',
              }}
            >
              {grayButtonText}
            </ButtonWithImage>
          )}
          <ButtonWithImage
            {...{
              className: 'blue-btn popup-confirm-button',
              buttonTheme: 'blue',
              onClick: handleBlueButton,
              'data-at': 'actionButton1',
              disabled: isBlueButtonDisabled,
              'data-testid': 'popup-confirm-button',
            }}
          >
            {blueButtonImg === 'phone' && <SPhoneIcon />}
            {blueButtonImg === 'email' && <SEmailIcon />}
            {blueButtonText}
          </ButtonWithImage>
        </SButtonsLine>
      )}

      {noteText && <Note>{noteText}</Note>}
    </SPopup>
  );

  if (noLightBox) return popup;
  return <Lightbox children={popup} />;
};

Popup.defaultProps = {
  noFooterButtons: false,
  isBlueButtonDisabled: false,
  grayButtonText: 'Exit',
  blueButtonText: 'No, Stay',
  boldText: '',
};
export default withTheme(Popup);
